/**
 * Exportación a Google Sheets, no autoritativa (revisión de ADR OQ-1,
 * 2026-09-10). IndexedDB local es la fuente de autoridad; Sheets pasa a ser
 * un destino de exportación visual y portable, nunca de sincronización ni de
 * cálculo. No hay lectura de vuelta hacia el libro: exportar nunca modifica
 * el almacén local.
 *
 * Dos pestañas con semántica distinta:
 *  - `Movimientos`: append-only, reutiliza `SheetsAdapter` (spec/03-data-model.md
 *    §4 — sin método de actualización ni borrado, INV-6 estructural). Conserva
 *    todo el histórico, incluidas reversiones: nunca se reescribe una fila.
 *  - `Resumen actual`: NO es el libro. Es una vista derivada (saldo vigente
 *    por cuenta) que el usuario puede regenerar a voluntad. Al no ser parte
 *    del libro append-only, su escritura puede reemplazar el contenido de la
 *    pestaña sin violar INV-6 — esa garantía protege `entries`, no un resumen
 *    explícitamente marcado como regenerable.
 */

import type { Account } from "../core/account.js";
import type { ChainedEntry } from "../integrity/chain.js";
import type { Entry } from "../core/entry.js";
import { accountBalance } from "../core/ledger.js";
import { type SheetsAdapter } from "./sheets-adapter.js";
import { toSheetRow } from "./sync-engine.js";

export interface SummaryRow {
  readonly accountId: string;
  readonly accountName: string;
  readonly accountType: string;
  readonly balanceMinor: bigint;
  readonly currency: string;
  readonly scale: number;
  readonly asOf: string; // instante UTC ISO-8601 en que se calculo el resumen
}

/**
 * Escritor de la pestaña "Resumen actual". A diferencia de `SheetsAdapter`
 * (append-only estructural para el libro), esta interfaz admite reemplazo
 * porque el contenido que escribe no es el libro: es una vista derivada,
 * siempre reconstruible desde `entries` (INV-4).
 */
export interface SummarySheetWriter {
  /** Sustituye por completo el contenido de la pestaña de resumen por `rows`. */
  replaceSummary(rows: readonly SummaryRow[]): Promise<void>;
}

const DEFAULT_SCALE = 2;

/** Calcula el saldo vigente de cada cuenta no archivada, derivado del libro (INV-4). Nunca escribe el libro. */
export function computeSummaryRows(
  accounts: readonly Account[],
  chainedEntries: readonly ChainedEntry[],
  asOf: string = new Date().toISOString(),
): SummaryRow[] {
  const entries: Entry[] = chainedEntries.map((c) => c.entry);
  return accounts
    .filter((a) => !a.archived)
    .map((account) => {
      const balance = accountBalance(account.accountId, entries, account.currency, DEFAULT_SCALE);
      return {
        accountId: account.accountId,
        accountName: account.name,
        accountType: account.type,
        balanceMinor: balance.amountMinor,
        currency: balance.currency,
        scale: balance.scale,
        asOf,
      };
    });
}

export interface ExportMovimientosOptions {
  /**
   * "update-existing" anexa solo lo que aún no está en la hoja (idempotente,
   * misma hoja reutilizada). "new-dated" trata el adaptador como una hoja
   * nueva y fechada: el llamador es responsable de apuntar `adapter` a esa
   * hoja nueva antes de llamar — esta función siempre exporta el histórico
   * completo pasado en `entries`, apto tanto para "actualizar" como para
   * "generar una exportación fechada nueva" (elección del usuario, item 5).
   */
  readonly mode: "update-existing" | "new-dated";
}

export interface ExportMovimientosResult {
  readonly mode: "update-existing" | "new-dated";
  readonly exported: number;
  readonly skippedAlreadyPresent: number;
}

/**
 * Exporta el histórico de asientos a la pestaña "Movimientos", append-only.
 * En "update-existing" solo anexa entry_id no presentes (idempotente); en
 * "new-dated" el adaptador ya apunta a una hoja/pestaña nueva y vacía, así
 * que todo se anexa. Nunca lee tokens ni escribe nada fuera de `SheetRow`.
 */
export async function exportMovimientos(
  adapter: SheetsAdapter,
  entries: readonly ChainedEntry[],
  options: ExportMovimientosOptions,
): Promise<ExportMovimientosResult> {
  const existingIds = options.mode === "update-existing" ? await adapter.listExistingEntryIds() : new Set<string>();
  const toExport = entries.filter((e) => !existingIds.has(e.entry.entryId));
  await adapter.appendEntries(toExport.map(toSheetRow));
  return {
    mode: options.mode,
    exported: toExport.length,
    skippedAlreadyPresent: entries.length - toExport.length,
  };
}

/** Regenera por completo la pestaña "Resumen actual" a partir del libro vigente. Nunca toca `entries`. */
export async function exportResumen(
  writer: SummarySheetWriter,
  accounts: readonly Account[],
  entries: readonly ChainedEntry[],
  asOf?: string,
): Promise<{ rowsWritten: number }> {
  const rows = computeSummaryRows(accounts, entries, asOf);
  await writer.replaceSummary(rows);
  return { rowsWritten: rows.length };
}

/** Doble de prueba: en memoria, admite reemplazo (coherente con la semántica no-autoritativa del resumen). */
export class FakeSummarySheetWriter implements SummarySheetWriter {
  private rows: SummaryRow[] = [];
  private replaceCount = 0;

  async replaceSummary(rows: readonly SummaryRow[]): Promise<void> {
    this.rows = [...rows];
    this.replaceCount += 1;
  }

  currentRows(): readonly SummaryRow[] {
    return this.rows;
  }

  timesReplaced(): number {
    return this.replaceCount;
  }
}
